import { useDialog } from 'muibox';
import { useContext } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { OnlyFansLists } from '../types/onlyFansTypes';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';
import { OnlyFansPosts } from './useSubscriberPosts';

// ---- Domain Types (copied/adapted from your prompt) ----
export type Status = 'ACTIVE' | 'INACTIVE';
type ListRef = { listId: number | OnlyFansLists.Type };
type PostLabel = { postLabelId: number | string; labelName: string };

export interface InventoryVersion {
    onlyfansId: number;
    inventoryId: number;
    inventorySubId: number;
    versionName: string;
    text: string;
    price: number | string;
    autoAddList: string;
    status: Status;
    dateCreated: Date;
    postLabels?: PostLabel[];
    sendLists?: ListRef[];
    excludeLists?: ListRef[];
    creatorTags?: Array<{
        creatorOnlyfansId: number;
        creatorName: string;
    }>;
    tags?: Array<{
        tagText: string;
    }>;
    unlockedMedias?: Array<{
        mediaId: number;
        order: number;
    }>;
    lockedMedias?: Array<{
        mediaId: number;
        order: number;
    }>;
    additionalSettings?: InventoryVersionAdditionalSettings;
}

export type ConsentForm = {
    id: number;
    name: string;
    type?: string;
    userPartnerId?: number;
};

export interface InventoryVersionAdditionalSettings {
    consentForms?: ConsentForm[];
    autoUnsendHours?: number | null;
}

export interface ContentInventory {
    onlyfansId: bigint | number;
    inventoryId: number;
    name: string;
    status: Status;
    versions: InventoryVersion[];
}

export interface NewInventoryDto {
    onlyfansId: number;
    name: string;
    versionName: string;
    massMessageId?: number;
}

export interface NewInventoryVersionDto {
    onlyfansId: number;
    inventoryId: number;
    versionName: string;
    sourceSubId?: number;
}

export interface UpdateInventoryStatusDto {
    onlyfansId: number;
    inventoryId: number;
    status: Status;
}

const useInventory = () => {
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const axios = useAxios();
    const dialog = useDialog();
    const userContext = useContext(UserContext);

    const getContentInventory = async (): Promise<ContentInventory[]> => {
        const query: string = `${settingsContext.routes.inventory.base}${params.userId}`;

        const data = await axios
            .get(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return [];
            });
        return data;
    };

    const newContentInventoryItem = async (
        name: string,
        versionName: string,
        massMessageId?: number,
    ): Promise<ContentInventory | null> => {
        const query: string = `${settingsContext.routes.inventory.base}${params.userId}/new`;
        const newInventoryPayload: NewInventoryDto = {
            onlyfansId: 0,
            name,
            versionName,
            massMessageId,
        };
        const data = await axios
            .post(query, newInventoryPayload)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return null;
            });
        return data;
    };
    const newContentInventoryVersion = async (
        inventoryId: number,
        versionName: string,
        sourceSubId?: number,
    ): Promise<InventoryVersion | null> => {
        const query: string = `${settingsContext.routes.inventory.base}${params.userId}/newversion`;
        const newInventoryPayload: NewInventoryVersionDto = {
            onlyfansId: 0,
            inventoryId,
            versionName,
            sourceSubId,
        };
        const data = await axios
            .post(query, newInventoryPayload)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return null;
            });
        return data;
    };

    const updateInventory = async (item: ContentInventory): Promise<boolean> => {
        const query: string = `${settingsContext.routes.inventory.base}${params.userId}/update`;
        const data = await axios
            .post(query, item)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const deleteInventory = async (inventoryId: number): Promise<boolean> => {
        const query: string = `${settingsContext.routes.inventory.base}${params.userId}/statusupdate`;
        const payload: UpdateInventoryStatusDto = {
            onlyfansId: 0,
            inventoryId,
            status: 'INACTIVE',
        };
        const data = await axios
            .post(query, payload)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const mergeSendLists = (prev: ListRef[] | undefined, selected: OnlyFansLists.List[]): ListRef[] => {
        // Key by String(listId): the saved model round-trips through the API where numeric ids can come
        // back as strings (or vice versa), and a strict-typed Map key would fail to match them.
        const byId = new Map(prev?.map(s => [String(s.listId), s]) ?? []);
        return selected.map(list => {
            const existing = byId.get(String(list.id));
            return existing || { listId: list.id, listName: list.name };
        });
    };
    const mergePostLabels = (prev: PostLabel[] | undefined, selected: OnlyFansPosts.CreatorPostLabels[]): PostLabel[] => {
        const byId = new Map(prev?.map(s => [s.postLabelId, s]) ?? []);
        return selected.map(list => {
            const existing = byId.get(list.labelId);
            return existing || { postLabelId: list.labelId, labelName: list.name };
        });
    };

    const mergeTaggedCreators = (
        prev: Array<{ creatorOnlyfansId: number; creatorName: string }> | undefined,
        selected: { id: number; name: string }[],
    ): Array<{
        creatorOnlyfansId: number;
        creatorName: string;
    }> => {
        const byId = new Map(prev?.map(s => [s.creatorOnlyfansId, s]) ?? []);
        return selected.map(list => {
            const existing = byId.get(list.id);
            return existing || { creatorOnlyfansId: list.id, creatorName: list.name };
        });
    };

    // Merge selected release forms ({ id, name }) back into consentForms, preserving any
    // extra fields (type/userPartnerId) already stored for forms that remain selected.
    const mergeConsentForms = (prev: ConsentForm[] | undefined, selected: { id: number; name: string }[]): ConsentForm[] => {
        const byId = new Map(prev?.map(s => [s.id, s]) ?? []);
        return selected.map(item => {
            const existing = byId.get(item.id);
            return existing || { id: item.id, name: item.name };
        });
    };

    const theContentInventory = useQuery(
        ['theContentInventory', params.userId],
        () => {
            return getContentInventory();
        },
        {
            staleTime: 60 * 1000 * 15, // 15 minutes
            refetchOnWindowFocus: false,
            enabled: !!(userContext.jwtToken && params.userId && settingsContext.apiKey),
        },
    );

    return {
        ...theContentInventory,
        newContentInventoryItem,
        newContentInventoryVersion,
        mergeSendLists,
        mergePostLabels,
        mergeTaggedCreators,
        mergeConsentForms,
        updateInventory,
        deleteInventory,
    };
};
export default useInventory;
